import { Collection, Model, type QueryParams } from '@kp/js-model'
import { BaseModel } from '@/models/BaseModel'
import { BaseDataProxy } from '@/models/BaseDataProxy'
import { readonly, ref, type DeepReadonly, type Ref } from 'vue'
import type { PropertiesObject, QueryOptions } from '@kp/js-model/dist/SharedTypes'
import { instance as api } from '@/helpers/api'

export class BaseCollection<T extends BaseModel> extends Collection<T> {
    protected modelCls: any
    protected proxy: BaseDataProxy | null = null
    protected _loading: Ref<boolean> = ref(false)
    public loading: DeepReadonly<Ref<boolean>>

    public constructor() {
        super()
        const refModels: Ref<T[]> = ref([])
        this._models = refModels.value
        this.loading = readonly(this._loading)
    }

    public async query(
        queryParams?: QueryParams | null,
        opts?: QueryOptions | null,
    ): Promise<this> {
        this._loading.value = true
        try {
            await super.query(queryParams, opts)
        } finally {
            this._loading.value = false
        }
        return this
    }

    public getDataProxy(): BaseDataProxy {
        if (!this.proxy) {
            this.proxy = new BaseDataProxy()
        }
        return this.proxy
    }

    public getServerTotal(): number {
        return this.proxy?.serverTotal ?? 0
    }
}

/**
 * Generic Collection for the /lookup/ routes:
 *
 * Provide the model you want in the constructor, e.g.
 *
 * l = new LookupCollection(null, {model: Ort});
 * l.query('foo'); ==> request to: /api/lookup/ort?query=foo
 */
export class LookupCollection<M extends BaseModel> extends BaseCollection<M> {
    protected proxy: LookupDataProxy | null = null

    public getDataProxy(): LookupDataProxy {
        if (!this.proxy) {
            this.proxy = new LookupDataProxy()
        }
        return this.proxy
    }

    public async queryValue(queryParams?: QueryParams | null): Promise<this> {
        this._loading.value = true
        try {
            const res = await this.getDataProxy().queryValue(queryParams)
            res.forEach((item) => {
                this.push(item)
                const last = this.last()
                if (last) {
                    last.setPhantom(false)
                    last.commit()
                }
            })
        } finally {
            this._loading.value = false
        }
        return this
    }
}

class LookupDataProxy extends BaseDataProxy {
    async fetch<M extends Model>(
        m: M,
        queryParams?: QueryParams,
    ): Promise<PropertiesObject | null> {
        throw new Error('not allowed')
    }

    async create<M extends Model>(
        m: M,
        queryParams?: QueryParams,
    ): Promise<PropertiesObject | null> {
        throw new Error('not allowed')
    }

    async update<M extends Model>(
        m: M,
        queryParams?: QueryParams,
    ): Promise<PropertiesObject | null> {
        throw new Error('not allowed')
    }

    async delete<M extends Model>(
        m: M,
        queryParams?: QueryParams,
    ): Promise<PropertiesObject | null> {
        throw new Error('not allowed')
    }

    async query<M extends Model, C extends Collection<M>>(
        collection: C,
        queryParams?: QueryParams,
    ): Promise<PropertiesObject[]> {
        const response = await api().post(
            `/lookup/${collection.getModelClassName()}`,
            queryParams,
            {
                cancelToken: this.createCancelToken(),
            },
        )
        this.serverTotal = response?.data?.total || 0

        return response?.data?.records || []
    }

    async queryValue(queryParams?: QueryParams | null): Promise<PropertiesObject[]> {
        const response = await api().post(`/lookup/queryValues`, queryParams, {
            cancelToken: this.createCancelToken(),
        })
        this.serverTotal = response?.data?.total || 0

        return response?.data?.records || []
    }
}
